import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  createTagSchema,
  updateTagSchema,
  type CreateTagFormData,
  type UpdateTagFormData,
} from '@/lib/validations/auth'

interface TagFormProps {
  tagId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type TagFormValues = CreateTagFormData | UpdateTagFormData

export function TagForm({ tagId, onSuccess, onCancel }: TagFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!tagId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<TagFormValues>({
    resolver: zodResolver(isEditMode ? updateTagSchema : createTagSchema),
    defaultValues: {
      descriptions: '',
      slug: '',
    },
  })

  const nameValue = watch('name')
  useEffect(() => {
    if (!isEditMode && nameValue) {
      const slug = nameValue
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
      setValue('slug', slug, { shouldValidate: false })
    }
  }, [nameValue, isEditMode, setValue])

  useEffect(() => {
    if (isEditMode && tagId) {
      setIsLoading(true)
      apiClient
        .get(`/content-management/tags/${tagId}`)
        .then(({ data }) => {
          const tagData = data?.data || data || {}
          reset({
            name: tagData.name || '',
            slug: tagData.slug || '',
            descriptions: tagData.descriptions || '',
          })
        })
        .catch((error: any) => {
          showError(error)
        })
        .finally(() => setIsLoading(false))
    }
  }, [isEditMode, tagId, reset, showError])

  const onSubmit = async (data: TagFormValues) => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: data.name,
        slug: data.slug || null,
        descriptions: data.descriptions || null,
      }

      if (isEditMode) {
        await apiClient.put(`/content-management/tags/${tagId}`, payload)
        showSuccess('Success', 'Tag updated successfully')
      } else {
        await apiClient.post('/content-management/tags', payload)
        showSuccess('Success', 'Tag created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/content-management/tags')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/content-management/tags')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading tag...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g. Technology, Business, Health" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            <FormHelperText>The name is how it appears on your site.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Slug
          </FormLabel>
          <FormControl isInvalid={!!errors.slug}>
            <Input {...register('slug')} placeholder="e.g. technology, business, health" />
            <FormErrorMessage>{errors.slug?.message}</FormErrorMessage>
            <FormHelperText>The slug is used in URLs. Auto-generated from name, but you can customize it.</FormHelperText>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>
          <FormControl isInvalid={!!errors.descriptions}>
            <Textarea {...register('descriptions')} placeholder="Enter tag description..." rows={4} />
            <FormErrorMessage>{errors.descriptions?.message}</FormErrorMessage>
            <FormHelperText>Optional description for this tag.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create Tag'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}


